import {Loader2Icon, PlusIcon, Trash2Icon} from "lucide-react";
import {useEffect, useMemo, useState} from "react";
import {toast} from "sonner";

import {Badge} from "@components/ui/badge";
import {Button} from "@components/ui/button";
import {Card, CardContent, CardHeader, CardTitle} from "@components/ui/card";
import {Input} from "@components/ui/input";
import {Label} from "@components/ui/label";
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from "@components/ui/select";
import {Skeleton} from "@components/ui/skeleton";
import {Table, TableBody, TableCell, TableHead, TableHeader, TableRow} from "@components/ui/table";
import {
    commitOps,
    createCustomNode,
    deleteCustomNode,
    deleteOp,
    fetchVNextModelingWorkbenchExpand,
    type ModelingWorkbenchRow,
    patchScenario,
    useVNextCustomNodes,
    useVNextModelingWorkbench,
} from "@hooks/use-vnext";

interface DriversAssumptionsTabProps {
    workspaceId: string;
    scenarioId: string;
}

const ASSUMPTION_FIELDS: {key: string; label: string; hint: string; min: number; max: number}[] = [
    {key: "ar_days", label: "AR days (DSO)", hint: "Days to collect receivables", min: 0, max: 365},
    {key: "ap_days", label: "AP days (DPO)", hint: "Days to pay suppliers", min: 0, max: 365},
    {key: "credit_sales_pct", label: "Credit sales %", hint: "Share of revenue invoiced on terms", min: 0, max: 100},
    {key: "credit_purchases_pct", label: "Credit purchases %", hint: "Share of costs bought on terms", min: 0, max: 100},
    {key: "capex_monthly", label: "Capex / month ($)", hint: "Monthly capital expenditure", min: 0, max: 100_000_000},
    {key: "loan_cf_monthly", label: "Loan cash flow / month ($)", hint: "Net drawdowns (+) or repayments (−)", min: -100_000_000, max: 100_000_000},
];

const METHOD_OPTIONS: {value: string; label: string}[] = [
    {value: "avg_3m", label: "3-month average (T3M)"},
    {value: "avg_6m", label: "6-month average"},
    {value: "avg_12m", label: "12-month average (T12M)"},
    {value: "last_month", label: "Last month"},
    {value: "yoy_flat", label: "Same month prior year (SMPY)"},
    {value: "yoy_growth", label: "Prior year + growth"},
];

const DEFAULT_METHOD = "__default__";

const CUSTOM_CATEGORY_OPTIONS: {value: string; label: string}[] = [
    {value: "revenue.income", label: "Revenue"},
    {value: "costs.cogs", label: "Cost of Sales"},
    {value: "expenses.expense", label: "Operating Expenses"},
];

function categoryLabel(categoryKey: string): string {
    const prefix = categoryKey.split(".")[0];
    if (prefix === "revenue") return "Revenue";
    if (prefix === "costs") return "Cost of Sales";
    if (prefix === "expenses") return "OpEx";
    return prefix;
}

export function DriversAssumptionsTab({workspaceId, scenarioId}: DriversAssumptionsTabProps) {
    const {data, isLoading, error, refresh} = useVNextModelingWorkbench(workspaceId, scenarioId);
    const {nodes: customNodes, refresh: refreshCustomNodes} = useVNextCustomNodes(workspaceId);

    // ── Working capital assumptions ──────────────────────────────
    const scenarioAssumptions = data?.meta.scenario.assumptions;
    const [draft, setDraft] = useState<Record<string, string>>({});
    const [savingAssumptions, setSavingAssumptions] = useState(false);

    useEffect(() => {
        const next: Record<string, string> = {};
        for (const field of ASSUMPTION_FIELDS) {
            const v = scenarioAssumptions?.[field.key];
            next[field.key] = v === undefined ? "" : String(v);
        }
        setDraft(next);
    }, [scenarioAssumptions]);

    const saveAssumptions = async () => {
        const patch: Record<string, number | null> = {};
        for (const field of ASSUMPTION_FIELDS) {
            const raw = draft[field.key]?.trim() ?? "";
            if (raw === "") {
                if (scenarioAssumptions?.[field.key] !== undefined) patch[field.key] = null;
                continue;
            }
            const num = Number(raw);
            if (Number.isNaN(num)) {
                toast.error(`${field.label} must be a number`);
                return;
            }
            if (num < field.min || num > field.max) {
                toast.error(`${field.label} must be between ${field.min} and ${field.max}`);
                return;
            }
            patch[field.key] = num;
        }
        setSavingAssumptions(true);
        try {
            await patchScenario(workspaceId, {scenario_id: scenarioId, assumptions: patch});
            toast.success("Assumptions saved — forecast will use them on next run");
            await refresh();
        } catch (e) {
            toast.error(e instanceof Error ? e.message : "Failed to save assumptions");
        } finally {
            setSavingAssumptions(false);
        }
    };

    // ── Per-account method + churn ───────────────────────────────
    // The base workbench payload only carries collapsed section totals; account
    // rows must come from the section expand endpoint.
    const pnlSectionKeys = useMemo(
        () =>
            (data?.statements.pnl.rows ?? [])
                .filter((r) => r.row_type === "section_total" && r.node_id)
                .map((r) => r.node_id),
        [data],
    );
    const [accountRows, setAccountRows] = useState<ModelingWorkbenchRow[]>([]);
    useEffect(() => {
        if (!workspaceId || pnlSectionKeys.length === 0) {
            setAccountRows([]);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const results = await Promise.all(
                    pnlSectionKeys.map((key) =>
                        fetchVNextModelingWorkbenchExpand(workspaceId, "pnl", key, scenarioId),
                    ),
                );
                if (cancelled) return;
                const seen = new Set<string>();
                setAccountRows(
                    results
                        .flatMap((r) => r.rows)
                        .filter((r) => {
                            if (r.row_type !== "account" || !r.node_id) return false;
                            if (r.inline_level && r.inline_level !== "account") return false;
                            if (seen.has(r.node_id)) return false;
                            seen.add(r.node_id);
                            return true;
                        }),
                );
            } catch {
                if (!cancelled) setAccountRows([]);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [workspaceId, scenarioId, pnlSectionKeys]);

    const opsHistory = useMemo(() => data?.ops_history ?? [], [data]);

    const methodOpByNode = useMemo(() => {
        const map = new Map<string, {opId: string; method: string}>();
        for (const op of opsHistory) {
            if (!op.is_active || op.op_type !== "set_baseline" || !op.target_node_id) continue;
            const method = op.params?.method as string | undefined;
            if (method) map.set(op.target_node_id, {opId: op.id, method});
        }
        return map;
    }, [opsHistory]);

    const churnOpByNode = useMemo(() => {
        const map = new Map<string, {opId: string; effectiveFrom: string}>();
        for (const op of opsHistory) {
            if (!op.is_active || op.op_type !== "set_driver" || !op.target_node_id) continue;
            if ((op.params?.driver_type as string) !== "churn") continue;
            map.set(op.target_node_id, {
                opId: op.id,
                effectiveFrom: (op.params?.effective_from as string) ?? "",
            });
        }
        return map;
    }, [opsHistory]);

    const [busyNodeId, setBusyNodeId] = useState<string | null>(null);

    const changeMethod = async (nodeId: string, label: string, method: string) => {
        setBusyNodeId(nodeId);
        try {
            const existing = methodOpByNode.get(nodeId);
            if (method === DEFAULT_METHOD) {
                if (existing) {
                    await deleteOp(workspaceId, existing.opId);
                    toast.success(`${label}: back to scenario default method`);
                }
            } else {
                if (existing) await deleteOp(workspaceId, existing.opId);
                await commitOps(
                    workspaceId,
                    [
                        {
                            op_type: "set_baseline",
                            target_node_id: nodeId,
                            label: `${label}: baseline → ${method}`,
                            params: {method},
                        },
                    ],
                    scenarioId,
                );
                toast.success(`${label}: forecast method set`);
            }
            await refresh();
        } catch (e) {
            toast.error(e instanceof Error ? e.message : "Failed to update method");
        } finally {
            setBusyNodeId(null);
        }
    };

    const changeChurn = async (nodeId: string, label: string, monthKey: string) => {
        setBusyNodeId(nodeId);
        try {
            const existing = churnOpByNode.get(nodeId);
            if (existing) await deleteOp(workspaceId, existing.opId);
            if (monthKey) {
                await commitOps(
                    workspaceId,
                    [
                        {
                            op_type: "set_driver",
                            target_node_id: nodeId,
                            label: `${label}: churned from ${monthKey}`,
                            params: {driver_type: "churn", effective_from: monthKey},
                        },
                    ],
                    scenarioId,
                );
                toast.success(`${label}: forecast $0 from ${monthKey}`);
            } else if (existing) {
                toast.success(`${label}: churn removed`);
            }
            await refresh();
        } catch (e) {
            toast.error(e instanceof Error ? e.message : "Failed to update churn");
        } finally {
            setBusyNodeId(null);
        }
    };

    // ── New Business rows ────────────────────────────────────────
    const [newLabel, setNewLabel] = useState("");
    const [newCategory, setNewCategory] = useState(CUSTOM_CATEGORY_OPTIONS[0].value);
    const [creating, setCreating] = useState(false);

    const addCustomNode = async () => {
        if (!newLabel.trim()) {
            toast.error("Enter a row name");
            return;
        }
        setCreating(true);
        try {
            await createCustomNode(workspaceId, {label: newLabel.trim(), category_key: newCategory});
            toast.success(`Added "${newLabel.trim()}" — drive it with a method or driver`);
            setNewLabel("");
            await Promise.all([refreshCustomNodes(), refresh()]);
        } catch (e) {
            toast.error(e instanceof Error ? e.message : "Failed to add row");
        } finally {
            setCreating(false);
        }
    };

    const removeCustomNode = async (nodeId: string, label: string) => {
        try {
            await deleteCustomNode(workspaceId, nodeId);
            toast.success(`Removed "${label}"`);
            await Promise.all([refreshCustomNodes(), refresh()]);
        } catch (e) {
            toast.error(e instanceof Error ? e.message : "Failed to remove row");
        }
    };

    if (error) {
        return <p className="text-sm text-destructive p-4">{error}</p>;
    }
    if (isLoading || !data) {
        return (
            <div className="space-y-4 p-2">
                <Skeleton className="h-48 w-full" />
                <Skeleton className="h-72 w-full" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <Card>
                <CardHeader>
                    <CardTitle className="text-base">Working Capital &amp; Cash Drivers</CardTitle>
                    <p className="text-sm text-muted-foreground">
                        Scenario-level assumptions used by the 3-statement forecast. Leave a field blank to
                        use the value derived from your actuals.
                    </p>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {ASSUMPTION_FIELDS.map((field) => (
                            <div key={field.key} className="space-y-1">
                                <Label htmlFor={`assumption-${field.key}`}>{field.label}</Label>
                                <Input
                                    id={`assumption-${field.key}`}
                                    type="number"
                                    placeholder="Derived from actuals"
                                    value={draft[field.key] ?? ""}
                                    onChange={(e) =>
                                        setDraft((prev) => ({...prev, [field.key]: e.target.value}))
                                    }
                                />
                                <p className="text-xs text-muted-foreground">{field.hint}</p>
                            </div>
                        ))}
                    </div>
                    <Button onClick={saveAssumptions} disabled={savingAssumptions}>
                        {savingAssumptions && <Loader2Icon className="size-4 animate-spin" />}
                        Save assumptions
                    </Button>
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle className="text-base">Per-Account Forecast Methods &amp; Churn</CardTitle>
                    <p className="text-sm text-muted-foreground">
                        Override the forecast method for individual accounts, or mark a revenue account as
                        churned to forecast it at $0 from a given month.
                    </p>
                </CardHeader>
                <CardContent>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Account</TableHead>
                                <TableHead>Category</TableHead>
                                <TableHead className="w-56">Forecast method</TableHead>
                                <TableHead className="w-48">Churned from</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {accountRows.map((row) => {
                                const methodOp = methodOpByNode.get(row.node_id);
                                const churnOp = churnOpByNode.get(row.node_id);
                                const busy = busyNodeId === row.node_id;
                                return (
                                    <TableRow key={row.node_id}>
                                        <TableCell className="font-medium">{row.label}</TableCell>
                                        <TableCell>
                                            <Badge variant="outline">{categoryLabel(row.category_key)}</Badge>
                                        </TableCell>
                                        <TableCell>
                                            <Select
                                                value={methodOp?.method ?? DEFAULT_METHOD}
                                                onValueChange={(v) => changeMethod(row.node_id, row.label, v)}
                                                disabled={busy}
                                            >
                                                <SelectTrigger className="w-full">
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value={DEFAULT_METHOD}>Scenario default</SelectItem>
                                                    {METHOD_OPTIONS.map((m) => (
                                                        <SelectItem key={m.value} value={m.value}>
                                                            {m.label}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex items-center gap-1">
                                                <Input
                                                    type="month"
                                                    className="w-40"
                                                    value={churnOp?.effectiveFrom ?? ""}
                                                    disabled={busy}
                                                    onChange={(e) =>
                                                        changeChurn(row.node_id, row.label, e.target.value)
                                                    }
                                                />
                                                {churnOp && (
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        disabled={busy}
                                                        onClick={() => changeChurn(row.node_id, row.label, "")}
                                                        title="Remove churn"
                                                    >
                                                        <Trash2Icon className="size-4" />
                                                    </Button>
                                                )}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                            {accountRows.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={4} className="text-center text-sm text-muted-foreground">
                                        No P&amp;L accounts found — sync your data first.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle className="text-base">New Business Rows</CardTitle>
                    <p className="text-sm text-muted-foreground">
                        Add synthetic rows (e.g. "New Business") with no Xero history. They forecast at $0
                        until you drive them, and survive data re-syncs.
                    </p>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="flex flex-wrap items-end gap-2">
                        <div className="space-y-1">
                            <Label htmlFor="custom-node-label">Row name</Label>
                            <Input
                                id="custom-node-label"
                                className="w-56"
                                placeholder="e.g. New Business"
                                value={newLabel}
                                onChange={(e) => setNewLabel(e.target.value)}
                            />
                        </div>
                        <div className="space-y-1">
                            <Label>Section</Label>
                            <Select value={newCategory} onValueChange={setNewCategory}>
                                <SelectTrigger className="w-48">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {CUSTOM_CATEGORY_OPTIONS.map((c) => (
                                        <SelectItem key={c.value} value={c.value}>
                                            {c.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <Button onClick={addCustomNode} disabled={creating}>
                            {creating ? (
                                <Loader2Icon className="size-4 animate-spin" />
                            ) : (
                                <PlusIcon className="size-4" />
                            )}
                            Add row
                        </Button>
                    </div>
                    {customNodes.length > 0 && (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Row</TableHead>
                                    <TableHead>Section</TableHead>
                                    <TableHead className="w-16" />
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {customNodes.map((n) => (
                                    <TableRow key={n.id}>
                                        <TableCell className="font-medium">{n.label}</TableCell>
                                        <TableCell>
                                            <Badge variant="outline">{categoryLabel(n.category_key)}</Badge>
                                        </TableCell>
                                        <TableCell>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                onClick={() => removeCustomNode(n.id, n.label)}
                                                title="Remove row"
                                            >
                                                <Trash2Icon className="size-4" />
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
